/**
 * Client for the Client-Learning endpoints (/v1/learning/*). Same server +
 * Firebase Bearer pattern as src/features/audit/api.ts.
 */
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import type { QueueItem, Progress, Variant, SubmitBody, SubmitResult } from "./types";

const API_URL = import.meta.env.VITE_QA_API_URL || "http://localhost:3100";

async function authed(path: string, init: RequestInit = {}): Promise<Response> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error("401 Not signed in");
  const token = await user.getIdToken();
  return fetch(`${API_URL}${path}`, {
    ...init,
    headers: { ...init.headers, "Content-Type": "application/json", Authorization: `Bearer ${token}` },
  });
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const d = await res.json();
      msg = d?.message || d?.error || msg;
    } catch {
      /* keep status */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export async function getVariant(): Promise<Variant> {
  return (await asJson<{ variant: Variant }>(await authed("/v1/learning/variant"))).variant;
}

export async function getQueue(organizationId: string): Promise<QueueItem[]> {
  const res = await authed(`/v1/learning/queue?organizationId=${encodeURIComponent(organizationId)}`);
  return (await asJson<{ findings: QueueItem[] }>(res)).findings;
}

export async function getProgress(): Promise<Progress> {
  return asJson<Progress>(await authed("/v1/learning/progress"));
}

export async function submitLabel(body: SubmitBody): Promise<SubmitResult> {
  return asJson<SubmitResult>(await authed("/v1/learning/labels", { method: "POST", body: JSON.stringify(body) }));
}
